import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { CalendarHeart, Wallet, Users, ListChecks, Store, MapPin, ArrowUpRight, Clock, KeyRound } from "lucide-react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useWedding } from "@/context/WeddingContext";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { StatCard, Progress, PageSkeleton, AnimatedNumber } from "@/components/shared";

const SAGE = "#8A9A86", GOLD = "#C5A880", BLUSH = "#E8D8D0", CHARCOAL = "#2C352D", TAUPE = "#D4C4B7";

export default function Dashboard() {
  const { wedding } = useWedding();
  const { hasAccess } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return <PageSkeleton />;
  const cur = wedding?.currency || "AUD";
  const g = data.guests, b = data.budget, t = data.tasks;

  const rsvpData = [
    { name: "Attending", value: g.attending, color: SAGE },
    { name: "Awaiting", value: g.awaiting, color: TAUPE },
    { name: "Maybe", value: g.maybe, color: GOLD },
    { name: "Declined", value: g.declined, color: BLUSH },
  ].filter((d) => d.value > 0);

  const budgetChart = data.charts.budget_by_category.filter((c) => c.planned > 0 || c.spent > 0).slice(0, 8);

  return (
    <div>
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">Welcome back</p>
        <h1 className="text-4xl sm:text-5xl tracking-tight text-primary leading-tight">
          {wedding?.partner1 || "Partner 1"} <span className="text-accent">&</span> {wedding?.partner2 || "Partner 2"}
        </h1>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-sm text-muted-foreground">
          {wedding?.date && <span className="inline-flex items-center gap-1.5"><CalendarHeart className="h-4 w-4" />{formatDate(wedding.date)}</span>}
          {wedding?.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" />{wedding.location}</span>}
          {wedding?.venue && <span className="inline-flex items-center gap-1.5"><Store className="h-4 w-4" />{wedding.venue}</span>}
          {wedding?.is_demo && <span className="text-accent font-medium">Demo planner</span>}
        </div>
      </div>

      {wedding?.is_demo && !hasAccess && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-2xl border border-accent/40 bg-accent/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="demo-banner">
          <div className="flex items-center gap-3">
            <span className="h-9 w-9 rounded-full bg-accent/20 flex items-center justify-center"><KeyRound className="h-4 w-4 text-accent" strokeWidth={1.5} /></span>
            <div>
              <div className="font-medium text-primary">You're exploring the demo</div>
              <div className="text-sm text-muted-foreground">Redeem your access code to start your own planner.</div>
            </div>
          </div>
          <Link to="/app/settings" data-testid="banner-unlock"><button className="rounded-full bg-primary text-primary-foreground px-5 py-2 text-sm hover:scale-[1.03] transition-transform">Unlock my planner</button></Link>
        </motion.div>
      )}

      {/* Countdown hero */}
      <motion.div
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="rounded-2xl bg-primary text-primary-foreground p-8 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 relative overflow-hidden">
        <div className="absolute -right-10 -top-10 h-48 w-48 rounded-full bg-accent/10 blur-2xl" />
        <div className="relative">
          <p className="text-xs uppercase tracking-[0.2em] text-primary-foreground/60">Wedding countdown</p>
          <div className="flex items-baseline gap-3 mt-2">
            <span className="text-6xl sm:text-7xl font-serif tnum leading-none">
              {data.days_to_go != null ? <AnimatedNumber value={Math.max(0, data.days_to_go)} /> : "—"}
            </span>
            <span className="text-lg text-primary-foreground/70">days to go</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-3 relative">
          {wedding?.colors?.slice(0, 6).map((c, i) => (
            <motion.span key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3 + i * 0.05, type: "spring" }}
              className="h-8 w-8 rounded-full border border-white/20" style={{ background: c }} />
          ))}
          {wedding?.style && <span className="rounded-full bg-white/10 px-4 py-2 text-sm self-center">{wedding.style}</span>}
        </div>
      </motion.div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard index={0} label="Budget" value={formatMoney(b.total, cur)} sub={`${formatMoney(b.spent, cur)} spent · ${formatMoney(b.remaining, cur)} left`} icon={Wallet} testId="stat-budget" />
        <StatCard index={1} label="Guests" value={`${g.attending}/${g.invited}`} sub={`${g.rsvp_percent}% RSVP responded`} icon={Users} testId="stat-guests" />
        <StatCard index={2} label="Tasks" value={`${t.completed}/${t.total}`} sub={`${t.remaining} remaining · ${t.overdue} overdue`} icon={ListChecks} testId="stat-tasks" />
        <StatCard index={3} label="Vendors booked" value={`${data.vendors.booked}/${data.vendors.total}`} sub="Suppliers confirmed" icon={Store} testId="stat-vendors" />
      </div>

      {/* Progress bars */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl bg-card border border-border p-6">
          <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Budget spent</span><span className="tnum font-medium">{b.percent_spent}%</span></div>
          <Progress value={b.percent_spent} />
          <p className="text-xs text-muted-foreground mt-2">{formatMoney(b.spent, cur)} of {formatMoney(b.total, cur)}</p>
        </div>
        <div className="rounded-2xl bg-card border border-border p-6">
          <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">RSVP responses</span><span className="tnum font-medium">{g.rsvp_percent}%</span></div>
          <Progress value={g.rsvp_percent} />
          <p className="text-xs text-muted-foreground mt-2">{g.attending + g.declined + g.maybe} of {g.invited} responded</p>
        </div>
        <div className="rounded-2xl bg-card border border-border p-6">
          <div className="flex justify-between text-sm mb-2"><span className="text-muted-foreground">Tasks complete</span><span className="tnum font-medium">{t.percent}%</span></div>
          <Progress value={t.percent} />
          <p className="text-xs text-muted-foreground mt-2">{t.completed} of {t.total} done</p>
        </div>
      </div>

      {/* Charts + upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="rounded-2xl bg-card border border-border p-6 lg:col-span-2">
          <h3 className="text-xl tracking-tight text-primary mb-4">Budget by category</h3>
          {budgetChart.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={budgetChart} margin={{ left: -12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#888" }} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "#888" }} />
                <Tooltip formatter={(v) => formatMoney(v, cur)} contentStyle={{ borderRadius: 12, border: "1px solid #E5E0D8" }} />
                <Bar dataKey="planned" name="Planned" fill={TAUPE} radius={[4, 4, 0, 0]} />
                <Bar dataKey="spent" name="Spent" fill={GOLD} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-sm text-muted-foreground py-10 text-center">Add budget categories to see this chart.</p>}
        </div>

        <div className="rounded-2xl bg-card border border-border p-6">
          <h3 className="text-xl tracking-tight text-primary mb-4">RSVP status</h3>
          {rsvpData.length ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={rsvpData} dataKey="value" innerRadius={45} outerRadius={72} paddingAngle={2}>
                    {rsvpData.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E5E0D8" }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {rsvpData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-sm">
                    <span className="inline-flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: d.color }} />{d.name}</span>
                    <span className="tnum text-muted-foreground">{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-sm text-muted-foreground py-10 text-center">Add guests to see RSVP breakdown.</p>}
        </div>
      </div>

      {/* Upcoming */}
      <div className="rounded-2xl bg-card border border-border p-6 mt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl tracking-tight text-primary">Upcoming deadlines & payments</h3>
          <Link to="/app/timeline" className="text-sm text-accent inline-flex items-center gap-1 hover:underline">View all <ArrowUpRight className="h-3.5 w-3.5" /></Link>
        </div>
        {data.upcoming.length ? (
          <div className="divide-y divide-border">
            {data.upcoming.map((u, i) => (
              <div key={`${u.type}-${u.date}-${u.title}-${i}`} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <span className="h-9 w-9 rounded-full bg-secondary flex items-center justify-center">{u.type === "payment" ? <Wallet className="h-4 w-4 text-accent" /> : <Clock className="h-4 w-4 text-accent" />}</span>
                  <div><div className="text-sm font-medium">{u.title}</div><div className="text-xs text-muted-foreground capitalize">{u.type} · {formatDate(u.date)}</div></div>
                </div>
                {u.amount != null && <span className="tnum text-sm font-medium">{formatMoney(u.amount, cur)}</span>}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-muted-foreground py-6 text-center">Nothing due soon. You're on track.</p>}
      </div>
    </div>
  );
}
